/* eslint-disable no-underscore-dangle */
import axios from 'axios';
import _debounce from 'lodash/debounce';
import _isEmpty from 'lodash/isEmpty';

import { CMP_SUBPAGES, CPP_SUBPAGES, HttpRoutes } from 'utils/constant';
import { ENTITY_TYPE } from 'utils/constants-v2';

import * as Utils from '../utils';
import { isBlankString } from '../utils';

import CommonService from './commonService';

const CommonConstants = {
  MAX_MAJOR_LENGTH: 91,
  MAX_COMPANY_LENGTH: 80,
  MAX_TITLE_LENGTH: 78,
  MAX_LOCATION_LENGTH: 45,
  MAX_COLLEGE_LENGTH: 91,
  MAX_SKILL_LENGTH: 45,
  MAX_CERTIFICATIONS_LENGTH: 90,
  AUTOCOMPLETE_DEBOUNCE_MS: 50,
  MAX_LANGUAGE_LENGTH: 35,
};

function generateParams(options = {}) {
  return Object.keys(options)
    .filter((key) => options[key] !== undefined)
    .reduce((params, key) => `${params}&${key}=${options[key]}`, '');
}

const AutosuggestProxy = {
  // ('d' -> 'c' -> 'b' -> 'a') =>  'abcd'
  GetString: (head) =>
    head ? `${AutosuggestProxy.GetString(head.previousLetter)}${head.value}` : '',

  SourceHandler: (self) => {
    const TriggerQuery = _debounce(() => {
      if (!self._head || self._head.query.triggered) return;
      return self._head.query.trigger();
    }, CommonConstants.AUTOCOMPLETE_DEBOUNCE_MS);

    return (queryString) => {
      if (typeof queryString !== 'string' || isBlankString(queryString)) {
        self._head = null;
        return self._getSuggestionsForEmptyInput();
      }

      queryString = queryString.trim();

      const lastMatchedLetter = AutosuggestProxy.GetLastMatchedLetter(self._head, queryString);
      const matchedLetterCount = lastMatchedLetter ? lastMatchedLetter.index + 1 : 0;

      if (matchedLetterCount !== queryString.length) {
        const unmatchedLetters = queryString.substr(matchedLetterCount);
        self._head = Array.from(unmatchedLetters).reduce(
          (head, letter) => AutosuggestProxy.SearchForLetter(self._getMatches)(head, letter),
          lastMatchedLetter
        );
      } else {
        self._head = lastMatchedLetter;
      }

      TriggerQuery();

      return self._head.query.promise.catch(() => []).then((matches) => matches);
    };
  },

  InList: (self) => (selection) => {
    if (!self._head) {
      return self
        ._getSuggestionsForEmptyInput()
        .some((suggestion) => suggestion.display_name === selection.display_name);
    }
    if (!self._head.query.result) return false;
    return self._head.query.result.some(
      (suggestion) => suggestion.display_name === selection.display_name
    );
  },

  FirstSuggestion: (self) => () => {
    if (!self._head) return self._getSuggestionsForEmptyInput()[0];
    if (!self._head.query.result) return null;
    if (self._head.query.result.length === 0) return null;
    return self._head.query.result[0];
  },

  NoSuggestion: (self) => () => {
    if (!self._head) return self._getSuggestionsForEmptyInput().length === 0;
    if (!self._head.query.result) return false;
    return self._head.query.result.length === 0;
  },

  SearchForLetter: (getMatches) =>
    AutosuggestProxy.AddLetter((head, index, letter) => {
      const str = AutosuggestProxy.GetString(head) + letter;
      return AutosuggestProxy.LetterEntry(
        head,
        index,
        letter,
        AutosuggestProxy.Query(() => getMatches(str))
      );
    }),

  // queryFunc: () => Promise<[]>, the function that calls auto suggest API and returns suggestions
  /*  returns:
{
    promise: Promise<[]>, the promise that resolves to the suggestion list,
    triggered: boolean, shows if the request is already started,
    trigger: () => void, triggers request.
    result: [] | undefined, the result of query, is undefined until request completes
}
*/
  Query: (queryFunc) => {
    const self = {};
    self.promise = new Promise((resolve) => (self._resolve = resolve));
    self.trigger = () => {
      self.triggered = true;
      queryFunc().then((result) => {
        self._resolve(result);
        self.result = result;
      });
    };
    self.triggered = false;
    return self;
  },

  // createLetter: (previousLetter: LetterEntry, index: number, letter: string) => LetterEntry
  // previousLetter: LetterEntry
  // letter: string
  // If any of prefix letters is known to be no matched suggestions, will create a LetterEntry with no matched suggestions, otherwise create a new LetterEntry with 'createLetter' function
  AddLetter: (createLetter) => (previousLetter, letter) => {
    const indexOfPreviousLetter = previousLetter ? previousLetter.index : undefined;
    const index = indexOfPreviousLetter === undefined ? 0 : indexOfPreviousLetter + 1;

    return createLetter(previousLetter, index, letter);
  },

  // previousLetter: another LetterEntry
  // index: number
  // value: string, the letter
  // query: Query
  LetterEntry: (previousLetter, index, value, query) => ({
    previousLetter,
    index,
    value,
    query,
  }),

  // (('d' -> 'c' -> 'b' -> 'a'), 'ab') => ('b' -> 'a')
  GetLastMatchedLetter: (head, queryString) => {
    if (!head) {
      return undefined;
    }
    const cachedStringLength = head.index + 1;
    const queryStringLength = queryString.length;

    const startNode =
      cachedStringLength > queryStringLength
        ? AutosuggestProxy.TrimTail(head, cachedStringLength - queryStringLength)
        : head;

    const startString =
      queryStringLength > cachedStringLength
        ? queryString.substr(0, cachedStringLength)
        : queryString;

    const Inner = (head, str, matchStartLetter) => {
      if (head === undefined && isBlankString(str)) return matchStartLetter;

      const doesCurrentLetterMatch = head.value === str[str.length - 1];

      return doesCurrentLetterMatch
        ? Inner(head.previousLetter, str.substr(0, str.length - 1), matchStartLetter || head)
        : Inner(head.previousLetter, str.substr(0, str.length - 1), undefined);
    };
    return Inner(startNode, startString, undefined);
  },

  // (('d' -> 'c' -> 'b' -> 'a'), 2) => ('b' -> 'a')
  TrimTail: (letters, count) =>
    count === 0 ? letters : AutosuggestProxy.TrimTail(letters.previousLetter, count - 1),

  alternativeSuggest: (payload) => {
    const payloadBck = payload;
    const otherResults = [];

    if (!_isEmpty(payload)) {
      return new Promise((resolve, reject) => {
        CommonService.normalizedData(payload)
          .then((response) => {
            if (!response.data) return resolve(otherResults);
            if (
              (!payloadBck.organization || payloadBck.organization.length === 0) &&
              response.data.closest_lay_title &&
              response.data.closest_lay_title !== 'N/A'
            ) {
              const Object = {
                original_name: response.data.closest_lay_title,
                display_name: response.data.closest_lay_title,
                value: response.data.closest_lay_title,
              };
              otherResults.push(Object);
            } else if (response.data.major && response.data.major !== 'N/A') {
              const Object = {
                display_name: response.data.major,
                name: response.data.major,
                value: response.data.major,
                original_name: response.data.major,
              };
              otherResults.push(Object);
            } else if (payloadBck.organization && response.data.normalizedCompanyName) {
              const Object = {
                companyName: response.data.normalizedCompanyName,
                companyID: response.data.companyID,
                key: response.data.normalizedCompanyName,
              };
              otherResults.push(Object);
            }
            resolve(otherResults);
          })
          .catch((err) => {
            reject(err);
          });
      });
    } else {
      return Promise.resolve(otherResults);
    }
  },

  // getMatches: (searchString: string) => Promise<SuggestionItem[]> , SuggestionItem usually looks like { display_name: string, ... }. The function that call auto suggestion API and returns a list of suggestions.
  // getSuggestionsForEmptyInput: null | () => SuggestionItem[]. When the input string is empty, will suggest the array returned from this function. If this function is not provided, use empty array instead.
  state: (getMatches, getSuggestionsForEmptyInput) => {
    const self = {};

    // LetterEntry, point to the last input letter.
    // Represents the current input state. For example, if current input is 'abcd', _head will be ('d' -> 'c' -> 'b' -> 'a')
    // In case there are no input (empty string), _head will be null/undefined
    self._head = undefined;
    self._getMatches = getMatches;
    self._getSuggestionsForEmptyInput = getSuggestionsForEmptyInput || (() => Promise.resolve([]));

    // () => string: last Input string
    self.lastInput = () => AutosuggestProxy.GetString(self._head);

    // (queryString) => array: For typeahead
    self.sourceHandler = AutosuggestProxy.SourceHandler(self);

    // {display_name: string, ...} => Boolean: Is giving item included in the LAST suggestion list.
    // If the API call for last input is not completed, the function always returns false.
    self.suggests = AutosuggestProxy.InList(self);

    // () => { display_name: string, ... } | null
    // Get the first suggestion for the last input
    // Null if no matches/ API call not yet completed
    self.firstSuggestion = AutosuggestProxy.FirstSuggestion(self);

    // () => boolean
    // true, if API call for last call completed and no matches found
    // false, if API call not completed or any results found
    self.noSuggestion = AutosuggestProxy.NoSuggestion(self);

    // () => Promise<Suggestion[]>
    // Returns a Promise that resolves to a list of suggestions for CURRENT input
    self.lastQuery = () => {
      if (!self._head) {
        return Promise.resolve([]);
      }

      if (!self._head.query.triggered) self._head.query.trigger();
      return self._head.query.promise;
    };
    return self;
  },

  forTitles: (options = {}) => {
    return AutosuggestProxy.state((queryString) => {
      if (queryString.length > CommonConstants.MAX_TITLE_LENGTH) {
        return Promise.resolve([]);
      }
      return AutosuggestProxy.getAutosuggestTitles(queryString, generateParams(options))
        .then((data) => {
          if (!data || !Array.isArray(data)) {
            return [];
          }
          return data.map((item) => {
            const name = item.name || item.display_name;
            return {
              ...item,
              name:
                item.original_name &&
                name.toLowerCase() !== item.original_name.toLowerCase() &&
                name.indexOf(`(${item.original_name})`) === -1
                  ? `${name} (${item.original_name})`
                  : name,
            };
          });
        })
        .catch((err) => {
          throw err;
        });
    });
  },

  forLocations: (options = {}) => {
    return AutosuggestProxy.state((queryString) => {
      if (queryString.length > CommonConstants.MAX_LOCATION_LENGTH) {
        return Promise.resolve([]);
      }
      return AutosuggestProxy.getAutosuggestLocations(queryString, generateParams(options))
        .then((data) => {
          if (!data || !Array.isArray(data)) {
            return [];
          }
          return data.map((item) => {
            const location = Utils.splitLocation(item.display_name);
            return {
              ...item,
              value: {
                city: location.city,
                state: location.stateAbbr,
              },
            };
          });
        })
        .catch((err) => {
          throw err;
        });
    });
  },

  forMajors: (options) => {
    return AutosuggestProxy.state((queryString) => {
      if (queryString.length > CommonConstants.MAX_MAJOR_LENGTH) {
        return Promise.resolve([]);
      }
      return AutosuggestProxy.getAutosuggestMajors(queryString, generateParams(options))
        .then((data) => {
          if (!data || !Array.isArray(data)) {
            return [];
          }
          return data.map((item) => ({
            ...item,
            display_name:
              item.name.toLowerCase() !== item.original_name.toLowerCase() &&
              item.display_name.indexOf(`(${item.original_name})`) === -1
                ? `${item.display_name} (${item.original_name})`
                : item.display_name,
            value: item,
          }));
        })
        .catch((err) => {
          throw err;
        });
    });
  },

  forCompanies: (options) => {
    return AutosuggestProxy.state((queryString) => {
      if (queryString.length > CommonConstants.MAX_COMPANY_LENGTH) {
        return Promise.resolve([]);
      }
      return AutosuggestProxy.getAutosuggestCompanies(
        encodeURIComponent(queryString),
        generateParams(options)
      )
        .then((data) => {
          if (!data || !Array.isArray(data)) {
            return [];
          }
          return data.map((item) => ({
            ...item,
            display_name: item.companyName,
            name: item.companyID,
            value: item,
          }));
        })
        .catch((err) => {
          throw err;
        });
    });
  },

  forColleges: () => {
    return AutosuggestProxy.state((queryString) => {
      if (queryString.length > CommonConstants.MAX_COLLEGE_LENGTH) {
        return Promise.resolve([]);
      }
      return AutosuggestProxy.getAutosuggestColleges(queryString)
        .then((data) => {
          if (!data || !Array.isArray(data)) {
            return [];
          }
          return data.map((item) => ({
            ...item,
            display_name:
              item.name.toLowerCase() !== item.name.toLowerCase() &&
              item.display_name.indexOf(`(${item.name})`) === -1
                ? `${item.display_name} (${item.name})`
                : item.display_name,
            value: item,
          }));
        })
        .catch((err) => {
          throw err;
        });
    });
  },

  forSkills: () => {
    return AutosuggestProxy.state((queryString) => {
      if (queryString.length > CommonConstants.MAX_SKILL_LENGTH) {
        return Promise.resolve([]);
      }

      return AutosuggestProxy.getAutosuggestSkills(queryString)
        .then((data) =>
          data.map((item) => ({
            ...item,
            value: item,
            display_name: item.name,
          }))
        )
        .catch((err) => {
          throw err;
        });
    });
  },

  forCertifications: () => {
    return AutosuggestProxy.state((queryString) => {
      if (queryString.length > CommonConstants.MAX_CERTIFICATIONS_LENGTH) {
        return Promise.resolve([]);
      }

      return AutosuggestProxy.getAutosuggestCertifications(queryString)
        .then((data) =>
          data.map((item) => ({
            ...item,
            value: item,
            display_name: item.name,
          }))
        )
        .catch((err) => {
          throw err;
        });
    });
  },

  forLanguages: () => {
    return AutosuggestProxy.state((queryString) => {
      if (queryString.length > CommonConstants.MAX_LANGUAGE_LENGTH) {
        return Promise.resolve([]);
      }

      return AutosuggestProxy.getAutosuggestLanguages(queryString)
        .then((data) =>
          data.map((item) => ({
            ...item,
            value: item,
            display_name: item.name,
          }))
        )
        .catch((err) => {
          throw err;
        });
    });
  },

  forSuggestions: (options) => {
    return AutosuggestProxy.state((queryString) => {
      if (queryString.length > CommonConstants.MAX_LANGUAGE_LENGTH) {
        return Promise.resolve([]);
      }

      return AutosuggestProxy.getAutosuggestSuggestions(
        encodeURIComponent(queryString),
        generateParams(options)
      )
        .then((suggestions = []) => {
          if (suggestions.length === 1) {
            const newSuggestions = [];
            const suggestionItem = suggestions[0];
            if (_isEmpty(suggestionItem)) return newSuggestions;
            if (suggestionItem.entityType === ENTITY_TYPE.COMPANY) {
              // Company Jobs Page
              if (suggestionItem.displayFlagOfCompanyJobsPage) {
                newSuggestions.push({
                  value: {
                    ...suggestionItem,
                    url: Utils.buildCppSubpageUrl(
                      suggestionItem.zippiaCppUrl,
                      '',
                      CPP_SUBPAGES.JOBS
                    ),
                  },
                  display_name: `${suggestionItem.companyName} - Jobs`,
                });
              }
              // Company Overview Page
              if (suggestionItem.indexable) {
                newSuggestions.push({
                  value: {
                    ...suggestionItem,
                    url: suggestionItem.zippiaCppUrl,
                  },
                  display_name: `${suggestionItem.companyName} - Company`,
                });
              }
              // Company Salary Page
              if (suggestionItem.displayFlagOfCSP) {
                newSuggestions.push({
                  value: {
                    ...suggestionItem,
                    url: Utils.buildCppSubpageUrl(
                      suggestionItem.zippiaCppUrl,
                      '',
                      CPP_SUBPAGES.SALARY
                    ),
                  },
                  display_name: `${suggestionItem.companyName} - Salary`,
                });
              }
              // Company Demographics Page
              if (suggestionItem.displayFlagOfCompanyDemographicsPage) {
                newSuggestions.push({
                  value: {
                    ...suggestionItem,
                    url: Utils.buildCppSubpageUrl(
                      suggestionItem.zippiaCppUrl,
                      '',
                      CPP_SUBPAGES.DEMOGRAPHICS
                    ),
                  },
                  display_name: `${suggestionItem.companyName} - Demographics`,
                });
              }
              // Company Reviews Page
              if (suggestionItem.displayFlagOfCompanyReviewsPage) {
                newSuggestions.push({
                  value: {
                    ...suggestionItem,
                    url: Utils.buildCppSubpageUrl(
                      suggestionItem.zippiaCppUrl,
                      '',
                      CPP_SUBPAGES.REVIEWS
                    ),
                  },
                  display_name: `${suggestionItem.companyName} - Reviews`,
                });
              }
            } else {
              // Career Jobs Page
              if (suggestionItem.title_indexinfo?.indexInfoOfJobsPage === 'INDEX') {
                newSuggestions.push({
                  value: {
                    ...suggestionItem,
                    url: Utils.buildCmpSubpagesUrls(suggestionItem.name, '', CMP_SUBPAGES.JOBS),
                  },
                  display_name: `${suggestionItem.name} - Jobs`,
                });
              }
              // Career Main Page
              if (suggestionItem.title_indexinfo?.indexInfo === 'INDEX') {
                newSuggestions.push({
                  value: {
                    ...suggestionItem,
                    url: Utils.buildCmpSubpagesUrls(suggestionItem.name, '', CMP_SUBPAGES.OVERVIEW),
                  },
                  display_name: `${suggestionItem.name} - Careers`,
                });
              }
              // Career Salary Page
              if (suggestionItem.title_indexinfo?.indexInfoOfSalaryPage === 'INDEX') {
                newSuggestions.push({
                  value: {
                    ...suggestionItem,
                    url: Utils.buildCmpSubpagesUrls(suggestionItem.name, '', CMP_SUBPAGES.SALARY),
                  },
                  display_name: `${suggestionItem.name} - Salary`,
                });
              }
              // Career How to Beome Page
              if (suggestionItem.title_indexinfo?.indexInfoOfHowToBecomePage === 'INDEX') {
                newSuggestions.push({
                  value: {
                    ...suggestionItem,
                    url: Utils.buildCmpSubpagesUrls(
                      suggestionItem.name,
                      '',
                      CMP_SUBPAGES.HOW_TO_BECOME,
                      undefined,
                      suggestionItem.article
                    ),
                  },
                  display_name: `${suggestionItem.name} - How To Become`,
                });
              }
              // Career Skills Page
              if (suggestionItem.title_indexinfo?.indexInfoOfSkillsPage === 'INDEX') {
                newSuggestions.push({
                  value: {
                    ...suggestionItem,
                    url: Utils.buildCmpSubpagesUrls(suggestionItem.name, '', CMP_SUBPAGES.SKILLS),
                  },
                  display_name: `${suggestionItem.name} - Skills`,
                });
              }
            }
            return newSuggestions;
          } else {
            return suggestions.map((item) => {
              let displayName = '';
              let url = '';
              if (item.entityType === ENTITY_TYPE.COMPANY) {
                // Company Overview Page
                if (item.displayFlagOfCompanyJobsPage) {
                  displayName = `${item.companyName} - Jobs`;
                  url = Utils.buildCppSubpageUrl(
                    item.zippiaCppUrl,
                    '',
                    CPP_SUBPAGES.JOBS
                  )
                }
                // Company Overview Page
                else if (item.indexable) {
                  displayName = `${item.companyName} - Company`;
                  url = item.zippiaCppUrl;
                } else {
                  displayName = item.companyName;
                }
              } else {
                // Career Jobs Page
                if (item.title_indexinfo?.indexInfoOfJobsPage === 'INDEX') {
                  displayName = `${item.name} - Jobs`;
                  url = Utils.buildCmpSubpagesUrls(item.name, '', CMP_SUBPAGES.JOBS);
                }
                // Career Main Page
                else if (item.title_indexinfo?.indexInfo === 'INDEX') {
                  displayName = `${item.name} - Careers`;
                  url = Utils.buildCmpSubpagesUrls(item.name, '', CMP_SUBPAGES.OVERVIEW);
                } else {
                  displayName = item.name;
                }
              }
              return {
                value: {
                  ...item,
                  url,
                },
                display_name: displayName,
              };
            });
          }
        })
        .catch((err) => {
          throw err;
        });
    });
  },

  getAutosuggestTitles: (stringSearch, parameters) => {
    const queryParams = parameters || '';
    if (
      window.__NEXT_REDUX_STORE__ &&
      window.__NEXT_REDUX_STORE__.getState &&
      window.__NEXT_REDUX_STORE__.getState()['searchFilter']['searchForParticularDepartment']
    ) {
      const dataSource =
        window.particularDepartments[
          window.__NEXT_REDUX_STORE__.getState()['searchFilter']['searchWithoutCallNumber']
        ]['salaries'];
      const results = [];
      stringSearch = stringSearch
        .replace('.', '\\.')
        .replace('(', '\\(')
        .replace('*', '\\*')
        .replace('?', '\\?')
        .replace('+', '\\+')
        .replace('[', '\\[')
        .replace(']', '\\]')
        .replace('{', '\\{')
        .replace('}', '\\}')
        .replace('^', '\\^')
        .replace('$', '\\$')
        .replace('|', '\\|')
        .replace('\\', '\\\\')
        .replace('/', '\\/');
      const reg = new RegExp(stringSearch, 'gi');
      for (let value of dataSource) {
        if (value.jobTitle.match(reg)) {
          results.push({
            display_name: value.jobTitle,
            name: value.jobTitle,
            original_name: value.jobTitle,
            value: {
              display_name: value.jobTitle,
              name: value.jobTitle,
              original_name: value.jobTitle,
            },
          });
        }
      }
      return Promise.resolve(results);
    } else {
      stringSearch = stringSearch.replace('&', '');
      const url = `${HttpRoutes.autocompleteTitles}?searchString=${stringSearch + queryParams}`;
      return axios
        .get(url)
        .then((response) => {
          if (
            (!response.data || response.data.length < 1) &&
            !parameters?.includes('indexableOnly=true')
          ) {
            const payload = { title: stringSearch };
            return AutosuggestProxy.alternativeSuggest(payload).then((result) => {
              return result;
            });
          }
          response.data = response.data.map((item) => ({
            ...item,
            display_name: item.name,
            original_name: item.original_name,
            value: item,
          }));
          return response.data;
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    }
  },

  getAutosuggestLocations: (stringSearch, parameters) => {
    const queryParams = parameters || '';
    const url = `${HttpRoutes.autocompleteLocations}?searchString=${stringSearch + queryParams}`;
    return axios
      .get(url)
      .then((response) => {
        if (!response.data || response.data.length < 1) {
          const payload = { location: stringSearch };
          return AutosuggestProxy.alternativeSuggest(payload).then((result) => result);
        }
        return response.data;
      })
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  getAutosuggestMajors: (stringSearch, parameters) => {
    const queryParams = parameters || '';
    const url = `${HttpRoutes.autocompleteMajors}?searchString=${stringSearch + queryParams}`;

    return axios
      .get(url)
      .then((response) => {
        if (!response.data || response.data.length < 1) {
          const payload = { major: stringSearch };
          return AutosuggestProxy.alternativeSuggest(payload).then((result) => result);
        }
        return response.data;
      })
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  getAutosuggestCompanies: (stringSearch, parameters) => {
    const queryParams = parameters || '';
    const url = `${HttpRoutes.autocompleteCompanies}?searchString=${stringSearch + queryParams}`;

    return axios
      .get(url)
      .then((response) => response.data)
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  getAutosuggestColleges: (stringSearch, parameters) => {
    const queryParams = parameters || '';
    const url = `${HttpRoutes.autocompleteColleges}?searchString=${stringSearch + queryParams}`;

    return axios
      .get(url)
      .then((response) => response?.data ?? [])
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  getAutosuggestSkills: (search, params = '') => {
    const url = `${HttpRoutes.autocompleteSkills}?searchString=${search}${params}`;

    return axios
      .get(url)
      .then((response) => {
        if (response?.data?.length) {
          return response.data;
        }

        return [];
      })
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  getAutosuggestCertifications: (search, params = '') => {
    const url = `${HttpRoutes.autocompleteCertifications}?searchString=${search}${params}`;

    return axios
      .get(url)
      .then((response) => {
        if (response?.data?.length) {
          return response.data;
        }
        return [];
      })
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  getAutosuggestLanguages: (search, params = '') => {
    return axios
      .get(`${HttpRoutes.autocompleteLanguages}?searchString=${search}${params}`)
      .then((response) => {
        if (response?.data?.length) {
          return response.data;
        }
        return [];
      })
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  getAutosuggestSuggestions: (search, params = '') => {
    return axios
      .get(`${HttpRoutes.autocompleteSuggestions}?searchString=${search}${params}`)
      .then((response) => {
        if (response?.data?.length) {
          return response.data;
        }
        return [];
      })
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },
};

export default AutosuggestProxy;
